import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import {
  BRAND_MARK_ICON_SIZE,
  BRAND_NAME_FONT_SIZE,
  buildInvoiceBrandMarkDataUrl,
  invoiceBrandMarkUrl,
} from '../config/invoiceBrand';
import { supabase } from '../lib/supabase';

// Company GSTIN and details
const COMPANY_GSTIN = '29AAEFZ0929R1ZA'; // Karnataka - State code 29
const COMPANY_NAME = 'Zykes Technologies LLP';
const COMPANY_ADDRESS = 'Flat No J 702, Concorde Manhattans, Phase 1, Electronic City, Bengaluru - 560100, Karnataka, India';
const COMPANY_STATE = 'Karnataka';

// SAC: software licensing / SaaS credits (confirm with CA; 997331 preferred for micro-SaaS)
const SAC_CODE = '997331';

/** Comparison-tab / email stone palette */
const INV = {
  pageBg: '#fafaf9',
  cardBg: '#ffffff',
  border: '#e8e0d4',
  title: '#1c1917',
  body: '#57534e',
  bodyDark: '#44403c',
  value: '#292524',
  eyebrow: '#78716c',
  muted: '#a8a29e',
  accent: '#15803d',
  tableHead: '#f5f5f4',
  font: "'Lexend', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
} as const;

// GST Rates - 18% total (CGST 9% + SGST 9% for same state, IGST 18% for different state)
// const GST_RATE_INTRA = { CGST: 9, SGST: 9 }; // Same state
// const GST_RATE_INTER = { IGST: 18 }; // Different state

export interface InvoiceLineItem {
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
}

export interface Invoice {
  id: string;
  invoice_number: string;
  user_id: string;
  order_id: string;
  transaction_id?: string;
  invoice_date: string;
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  customer_gstin?: string;
  customer_address?: string;
  customer_state?: string;
  items: InvoiceLineItem[];
  subtotal: number;
  cgst_rate?: number;
  cgst_amount?: number;
  sgst_rate?: number;
  sgst_amount?: number;
  igst_rate?: number;
  igst_amount?: number;
  tax_amount: number;
  discount_amount: number;
  total_amount: number;
  currency: string;
  payment_method?: string;
  payment_status: string;
  payment_date?: string;
  status: string;
  pdf_url?: string;
  email_sent: boolean;
  created_at: string;
}

export interface CreateInvoiceParams {
  order_id: string;
  transaction_id?: string;
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  customer_gstin?: string;
  customer_address?: string;
  customer_state?: string;
  items: InvoiceLineItem[];
  subtotal: number;
  tax_amount?: number;
  discount_amount?: number;
  total_amount: number;
  currency?: string;
  payment_method?: string;
  payment_status?: string;
  payment_date?: string;
  notes?: string;
}

/**
 * Create a new invoice
 */
export async function createInvoice(params: CreateInvoiceParams): Promise<Invoice | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('User not authenticated');
      return null;
    }

    // Generate invoice number
    const { data: invoiceNumber, error: invoiceNumError } = await supabase.rpc('generate_invoice_number');
    
    if (invoiceNumError) {
      console.error('Error generating invoice number:', invoiceNumError);
      return null;
    }

    // B2C GST-inclusive pricing: params.subtotal is the actual amount charged to the customer.
    // We back-calculate (reverse GST) to extract the pre-tax base and the GST components.
    const totalPaid = params.subtotal; // e.g. ₹29 — what was actually charged
    const basePricePreGST = parseFloat((totalPaid / 1.18).toFixed(2));

    const customerStateCode = params.customer_gstin ? params.customer_gstin.substring(0, 2) : '29';
    const isIntraState = customerStateCode === '29'; // Karnataka state code

    let gstData: Record<string, number>;
    if (isIntraState) {
      // Same state — CGST 9% + SGST 9% (back-calculated from total)
      const cgstAmount = parseFloat((basePricePreGST * 9 / 100).toFixed(2));
      const sgstAmount = parseFloat((basePricePreGST * 9 / 100).toFixed(2));
      gstData = {
        cgst_rate: 9,
        cgst_amount: cgstAmount,
        sgst_rate: 9,
        sgst_amount: sgstAmount,
        tax_amount: parseFloat((cgstAmount + sgstAmount).toFixed(2)),
      };
    } else {
      // Different state — IGST 18% (back-calculated from total)
      const igstAmount = parseFloat((basePricePreGST * 18 / 100).toFixed(2));
      gstData = {
        igst_rate: 18,
        igst_amount: igstAmount,
        tax_amount: igstAmount,
      };
    }

    // Items should show pre-tax unit prices
    const adjustedItems = params.items.map(item => ({
      ...item,
      unit_price: parseFloat((item.unit_price / 1.18).toFixed(2)),
      total: parseFloat((item.total / 1.18).toFixed(2)),
    }));

    const invoiceData = {
      invoice_number: invoiceNumber,
      user_id: user.id,
      order_id: params.order_id,
      transaction_id: params.transaction_id,
      invoice_date: new Date().toISOString(),
      customer_name: params.customer_name,
      customer_email: params.customer_email,
      customer_phone: params.customer_phone,
      customer_gstin: params.customer_gstin,
      customer_address: params.customer_address,
      customer_state: params.customer_state,
      items: adjustedItems,
      subtotal: basePricePreGST,         // pre-tax base (e.g. ₹24.58)
      discount_amount: params.discount_amount || 0,
      total_amount: totalPaid,           // actual charged amount (e.g. ₹29.00)
      currency: params.currency || 'INR',
      payment_method: params.payment_method || 'cashfree',
      payment_status: params.payment_status || 'paid',
      payment_date: params.payment_date || new Date().toISOString(),
      status: 'generated',
      notes: params.notes,
      email_sent: false,
      ...gstData,
    };

    const { data: invoice, error } = await supabase
      .from('invoices')
      .insert(invoiceData)
      .select()
      .single();

    if (error) {
      console.error('Error creating invoice:', error);
      return null;
    }

    console.log('✅ Invoice created:', invoice.invoice_number);
    return invoice;
  } catch (error) {
    console.error('Unhandled error creating invoice:', error);
    return null;
  }
}

/**
 * Get all invoices for current user
 */
export async function getUserInvoices(limit = 50, offset = 0): Promise<Invoice[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('User not authenticated');
      return [];
    }

    const { data: invoices, error } = await supabase
      .from('invoices')
      .select('*')
      .eq('user_id', user.id)
      .order('invoice_date', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) {
      console.error('Error fetching invoices:', error);
      return [];
    }

    return invoices || [];
  } catch (error) {
    console.error('Unhandled error fetching invoices:', error);
    return [];
  }
}

/**
 * Get a single invoice by ID
 */
export async function getInvoiceById(invoiceId: string): Promise<Invoice | null> {
  try {
    const { data: invoice, error } = await supabase
      .from('invoices')
      .select('*')
      .eq('id', invoiceId)
      .single();

    if (error) {
      console.error('Error fetching invoice:', error);
      return null;
    }

    // Mark as viewed
    await supabase
      .from('invoices')
      .update({ status: 'viewed' })
      .eq('id', invoiceId)
      .eq('status', 'generated');

    return invoice;
  } catch (error) {
    console.error('Unhandled error fetching invoice:', error);
    return null;
  }
}

/**
 * Get invoice by order ID
 */
export async function getInvoiceByOrderId(orderId: string): Promise<Invoice | null> {
  try {
    const { data: invoice, error } = await supabase
      .from('invoices')
      .select('*')
      .eq('order_id', orderId)
      .single();

    if (error) {
      console.error('Error fetching invoice by order ID:', error);
      return null;
    }

    return invoice;
  } catch (error) {
    console.error('Unhandled error fetching invoice:', error);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Invoice i18n labels — add more languages here as needed
// ---------------------------------------------------------------------------
type InvoiceLang = 'en' | 'pt' | 'es' | 'ru';

const INVOICE_LABELS: Record<InvoiceLang, Record<string, string>> = {
  en: {
    title: 'INVOICE',
    from: 'FROM (SELLER)', billTo: 'BILL TO', details: 'INVOICE DETAILS',
    date: 'Date', paymentDate: 'Payment Date', placeOfSupply: 'Place of Supply', status: 'Status',
    typeB2C: 'Type: B2C (Consumer)', gstin: 'GSTIN', state: 'State',
    colDesc: 'Description', colSac: 'SAC Code', colQty: 'Qty', colRate: 'Rate', colAmount: 'Amount',
    subtotal: 'Subtotal:', totalTax: 'Total Tax:', totalAmount: 'Total Amount:',
    notes: 'Notes', thanksPurchase: 'Thank you for your purchase!',
    thanksBusiness: 'Thank you for your business!', orderId: 'Order ID',
    computerGenerated: 'This is a computer-generated invoice and does not require a physical signature.',
    paid: 'PAID', na: 'N/A', noTax: 'No Tax (Export)',
    locale: 'en-IN',
  },
  pt: {
    title: 'FATURA',
    from: 'DO (VENDEDOR)', billTo: 'FATURAR A', details: 'DETALHES DA FATURA',
    date: 'Data', paymentDate: 'Data de Pagamento', placeOfSupply: 'Local de Fornecimento', status: 'Status',
    typeB2C: 'Tipo: B2C (Consumidor)', gstin: 'GSTIN', state: 'Estado',
    colDesc: 'Descrição', colSac: 'Cód. SAC', colQty: 'Qtd', colRate: 'Taxa', colAmount: 'Valor',
    subtotal: 'Subtotal:', totalTax: 'Total de Impostos:', totalAmount: 'Valor Total:',
    notes: 'Notas', thanksPurchase: 'Obrigado pela sua compra!',
    thanksBusiness: 'Obrigado pelo seu negócio!', orderId: 'ID do Pedido',
    computerGenerated: 'Esta é uma fatura gerada por computador e não requer assinatura física.',
    paid: 'PAGO', na: 'N/D', noTax: 'Sem Imposto (Exportação)',
    locale: 'pt-BR',
  },
  es: {
    title: 'FACTURA',
    from: 'DE (VENDEDOR)', billTo: 'FACTURAR A', details: 'DETALLES DE LA FACTURA',
    date: 'Fecha', paymentDate: 'Fecha de Pago', placeOfSupply: 'Lugar de Suministro', status: 'Estado',
    typeB2C: 'Tipo: B2C (Consumidor)', gstin: 'GSTIN', state: 'Estado',
    colDesc: 'Descripción', colSac: 'Cód. SAC', colQty: 'Cant.', colRate: 'Tarifa', colAmount: 'Importe',
    subtotal: 'Subtotal:', totalTax: 'Total Impuestos:', totalAmount: 'Importe Total:',
    notes: 'Notas', thanksPurchase: '¡Gracias por su compra!',
    thanksBusiness: '¡Gracias por su negocio!', orderId: 'ID de Orden',
    computerGenerated: 'Esta es una factura generada por computadora y no requiere firma física.',
    paid: 'PAGADO', na: 'N/D', noTax: 'Sin Impuesto (Exportación)',
    locale: 'es-ES',
  },
  ru: {
    title: 'СЧЁТ',
    from: 'ОТ (ПРОДАВЕЦ)', billTo: 'ПОКУПАТЕЛЬ', details: 'ДЕТАЛИ СЧЁТА',
    date: 'Дата', paymentDate: 'Дата оплаты', placeOfSupply: 'Место поставки', status: 'Статус',
    typeB2C: 'Тип: B2C (Потребитель)', gstin: 'GSTIN', state: 'Штат',
    colDesc: 'Описание', colSac: 'Код SAC', colQty: 'Кол.', colRate: 'Цена', colAmount: 'Сумма',
    subtotal: 'Подитог:', totalTax: 'Итого налог:', totalAmount: 'Итого:',
    notes: 'Примечания', thanksPurchase: 'Спасибо за покупку!',
    thanksBusiness: 'Спасибо за сотрудничество!', orderId: 'ID заказа',
    computerGenerated: 'Это счёт, сформированный компьютером; физическая подпись не требуется.',
    paid: 'ОПЛАЧЕНО', na: 'Н/Д', noTax: 'Без налога (Экспорт)',
    locale: 'ru-RU',
  },
};

function getCurrencySymbol(currency: string): string {
  const map: Record<string, string> = { INR: '₹', BRL: 'R$', USD: '$', EUR: '€', RUB: '₽' };
  return map[currency?.toUpperCase()] ?? currency ?? '₹';
}

/**
 * Generate HTML for invoice (for display and PDF generation).
 * Pass lang to produce a localised invoice for pt/es/ru users.
 */
export function generateInvoiceHTML(
  invoice: Invoice,
  lang: InvoiceLang = 'en',
  brandMarkSrc?: string,
): string {
  const L = INVOICE_LABELS[lang] ?? INVOICE_LABELS.en;
  const currencySymbol = getCurrencySymbol(invoice.currency ?? 'INR');
  const isINR = (invoice.currency ?? 'INR').toUpperCase() === 'INR';

  const formattedDate = new Date(invoice.invoice_date).toLocaleDateString(L.locale as string, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const isB2B = Boolean(invoice.customer_gstin);
  const hasInterstateTax = Boolean(invoice.igst_amount && invoice.igst_amount > 0);

  const fmt = (n: number) => `${currencySymbol}${n.toFixed(2)}`;
  const fmtDate = (d: string | undefined) =>
    d ? new Date(d).toLocaleDateString(L.locale as string, { day: 'numeric', month: 'long', year: 'numeric' }) : L.na;

  const markIconSize = BRAND_MARK_ICON_SIZE;
  const markSrc = brandMarkSrc ?? invoiceBrandMarkUrl();
  const notesText = invoice.notes?.trim() || L.thanksPurchase;

  return `
<!DOCTYPE html>
<html lang="${lang}">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${L.title} ${invoice.invoice_number}</title>
  <link href="https://fonts.googleapis.com/css2?family=Lexend:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    @page { size: A4; margin: 0; }
    body {
      font-family: ${INV.font};
      line-height: 1.45;
      color: ${INV.body};
      background: ${INV.pageBg};
      -webkit-font-smoothing: antialiased;
    }
    .invoice-container {
      width: 210mm;
      min-height: 297mm;
      margin: 0 auto;
      background: ${INV.cardBg};
      border: 1px solid ${INV.border};
      display: flex;
      flex-direction: column;
    }
    .invoice-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 24px;
      padding: 32px 44px 28px;
      border-bottom: 1px solid ${INV.border};
    }
    .brand {
      display: flex;
      align-items: center;
      gap: 14px;
      min-height: ${markIconSize}px;
      flex-shrink: 0;
    }
    .brand-mark {
      width: ${markIconSize}px;
      height: ${markIconSize}px;
      border-radius: 10px;
      flex-shrink: 0;
      display: block;
      object-fit: cover;
      object-position: top center;
    }
    .brand-name {
      font-size: ${BRAND_NAME_FONT_SIZE}px;
      font-weight: 700;
      letter-spacing: -0.025em;
      color: ${INV.title};
      line-height: ${markIconSize}px;
      white-space: nowrap;
    }
    .invoice-meta { text-align: right; }
    .eyebrow {
      font-size: 9px;
      font-weight: 700;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: ${INV.eyebrow};
      margin-bottom: 6px;
    }
    .invoice-number {
      font-size: 22px;
      font-weight: 800;
      letter-spacing: -0.03em;
      color: ${INV.title};
      line-height: 1.1;
    }
    .invoice-details { padding: 24px 44px 8px; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 24px; }
    .info-block h3 {
      font-size: 9px;
      font-weight: 700;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: ${INV.eyebrow};
      margin-bottom: 10px;
    }
    .info-block p {
      margin: 3px 0;
      color: ${INV.body};
      font-size: 10px;
      line-height: 1.55;
    }
    .info-block p strong { color: ${INV.title}; font-weight: 600; }
    .invoice-body { padding: 8px 44px 24px; flex: 1; }
    .items-table { width: 100%; border-collapse: collapse; margin: 16px 0 20px; }
    .items-table thead tr { background: ${INV.tableHead}; }
    .items-table th {
      padding: 10px 12px;
      text-align: left;
      font-weight: 600;
      font-size: 9px;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: ${INV.eyebrow};
      border-bottom: 1px solid ${INV.border};
    }
    .items-table th.text-center { text-align: center; }
    .items-table th.text-right { text-align: right; }
    .items-table td {
      padding: 12px;
      font-size: 10px;
      color: ${INV.bodyDark};
      border-bottom: 1px solid ${INV.border};
    }
    .items-table td.text-center { text-align: center; color: ${INV.body}; }
    .items-table td.text-right {
      text-align: right;
      font-variant-numeric: tabular-nums;
      color: ${INV.value};
      font-weight: 500;
    }
    .items-table th.text-right { font-variant-numeric: tabular-nums; }
    .totals-wrap { display: flex; justify-content: flex-end; margin-top: 8px; }
    .totals-section { width: 260px; }
    .total-row { display: flex; justify-content: space-between; gap: 16px; padding: 5px 0; }
    .total-label { font-size: 10px; color: ${INV.body}; font-weight: 500; }
    .total-amount {
      font-size: 10px;
      color: ${INV.value};
      font-weight: 600;
      font-variant-numeric: tabular-nums;
      min-width: 88px;
      text-align: right;
    }
    .grand-total {
      border-top: 1px solid ${INV.border};
      margin-top: 8px;
      padding-top: 10px;
    }
    .grand-total .total-label { font-size: 12px; color: ${INV.title}; font-weight: 700; }
    .grand-total .total-amount { font-size: 12px; color: ${INV.title}; font-weight: 800; }
    .notes-section { padding: 0 44px 16px; }
    .notes-heading {
      font-size: 9px;
      font-weight: 700;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: ${INV.eyebrow};
      margin-bottom: 6px;
    }
    .notes-text { font-size: 10px; color: ${INV.body}; line-height: 1.6; }
    .invoice-footer {
      margin-top: auto;
      padding: 20px 44px 32px;
      border-top: 1px solid ${INV.border};
      text-align: left;
    }
    .invoice-footer p { margin: 4px 0; font-size: 9px; color: ${INV.muted}; line-height: 1.5; }
    .invoice-footer .footer-thanks { font-size: 10px; color: ${INV.title}; font-weight: 600; margin-top: 8px; }
    @media print {
      body { background: white; margin: 0; }
      .invoice-container { border: 1px solid ${INV.border}; margin: 0; width: 210mm; min-height: 297mm; }
      @page { margin: 0; }
    }
  </style>
</head>
<body>
  <div class="invoice-container">
    <header class="invoice-header">
      <div class="brand">
        <img
          src="${markSrc}"
          alt=""
          class="brand-mark"
          width="${markIconSize}"
          height="${markIconSize}"
          draggable="false"
        />
        <span class="brand-name">GoGrabJob</span>
      </div>
      <div class="invoice-meta">
        <p class="eyebrow">Tax invoice</p>
        <p class="invoice-number">${invoice.invoice_number}</p>
      </div>
    </header>

    <div class="invoice-details">
      <div class="info-grid">
        <div class="info-block">
          <h3>${L.from}</h3>
          <p><strong>${COMPANY_NAME}</strong></p>
          <p>${COMPANY_ADDRESS}</p>
          <p><strong>${L.gstin}:</strong> ${COMPANY_GSTIN}</p>
          <p><strong>${L.state}:</strong> ${COMPANY_STATE} (29)</p>
          <p>support@gograbjob.com</p>
        </div>

        <div class="info-block">
          <h3>${L.billTo}</h3>
          <p><strong>${invoice.customer_name}</strong></p>
          <p>${invoice.customer_email}</p>
          ${invoice.customer_phone ? `<p>${invoice.customer_phone}</p>` : ''}
          ${isB2B && invoice.customer_gstin ? `
            <p><strong>${L.gstin}:</strong> ${invoice.customer_gstin}</p>
            ${invoice.customer_address ? `<p>${invoice.customer_address}</p>` : ''}
            ${invoice.customer_state ? `<p><strong>${L.state}:</strong> ${invoice.customer_state}</p>` : ''}
          ` : `<p>${L.typeB2C}</p>`}
        </div>

        <div class="info-block">
          <h3>${L.details}</h3>
          <p><strong>${L.date}:</strong> ${formattedDate}</p>
          <p><strong>${L.paymentDate}:</strong> ${fmtDate(invoice.payment_date)}</p>
          <p><strong>${L.placeOfSupply}:</strong> ${invoice.customer_state || COMPANY_STATE}</p>
          <p><strong>${L.orderId}:</strong> ${invoice.order_id}</p>
        </div>
      </div>
    </div>

    <div class="invoice-body">
      <table class="items-table">
        <thead>
          <tr>
            <th>${L.colDesc}</th>
            <th class="text-center">${L.colSac}</th>
            <th class="text-center">${L.colQty}</th>
            <th class="text-right">${L.colRate}</th>
            <th class="text-right">${L.colAmount}</th>
          </tr>
        </thead>
        <tbody>
          ${invoice.items.map(item => `
            <tr>
              <td>${item.description}</td>
              <td class="text-center">${isINR ? SAC_CODE : '-'}</td>
              <td class="text-center">${item.quantity}</td>
              <td class="text-right">${currencySymbol}${item.unit_price.toFixed(2)}</td>
              <td class="text-right">${currencySymbol}${item.total.toFixed(2)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>

      <div class="totals-wrap">
        <div class="totals-section">
          <div class="total-row">
            <div class="total-label">${L.subtotal}</div>
            <div class="total-amount">${fmt(invoice.subtotal)}</div>
          </div>
          ${isINR ? (hasInterstateTax ? `
            <div class="total-row">
              <div class="total-label">IGST @ ${invoice.igst_rate}%</div>
              <div class="total-amount">${fmt(invoice.igst_amount ?? 0)}</div>
            </div>
          ` : `
            <div class="total-row">
              <div class="total-label">CGST @ ${invoice.cgst_rate ?? 9}%</div>
              <div class="total-amount">${fmt(invoice.cgst_amount ?? 0)}</div>
            </div>
            <div class="total-row">
              <div class="total-label">SGST @ ${invoice.sgst_rate ?? 9}%</div>
              <div class="total-amount">${fmt(invoice.sgst_amount ?? 0)}</div>
            </div>
          `) : `
            <div class="total-row">
              <div class="total-label">${L.noTax}</div>
              <div class="total-amount">${fmt(0)}</div>
            </div>
          `}
          <div class="total-row grand-total">
            <div class="total-label">${L.totalAmount}</div>
            <div class="total-amount">${fmt(invoice.total_amount)}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="notes-section">
      <h3 class="notes-heading">${L.notes}</h3>
      <p class="notes-text">${notesText}</p>
    </div>

    <footer class="invoice-footer">
      <p>${L.computerGenerated}</p>
      <p class="footer-thanks">${L.thanksBusiness}</p>
    </footer>
  </div>
</body>
</html>
  `;
}

/**
 * Mark invoice as downloaded
 */
export async function markInvoiceDownloaded(invoiceId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('invoices')
      .update({ 
        status: 'downloaded',
        pdf_generated_at: new Date().toISOString()
      })
      .eq('id', invoiceId);

    if (error) {
      console.error('Error marking invoice as downloaded:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Unhandled error marking invoice as downloaded:', error);
    return false;
  }
}

/**
 * Download invoice as PDF with proper GST details.
 * @param lang - BCP47 language code (en/pt/es/ru) for localised labels
 */
export async function downloadInvoicePDF(invoice: Invoice, lang: InvoiceLang = 'en'): Promise<void> {
  try {
    // Create an iframe completely isolated from the main page to avoid visual glitch
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.left = '-9999px';
    iframe.style.top = '0';
    iframe.style.width = '210mm';
    iframe.style.height = '297mm';
    iframe.style.border = 'none';
    iframe.style.zIndex = '-9999';
    iframe.style.visibility = 'hidden';
    iframe.style.opacity = '0';
    
    // Add to body
    document.body.appendChild(iframe);
    
    // Wait for iframe to be ready
    await new Promise(resolve => setTimeout(resolve, 50));
    
    // Get iframe document
    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!iframeDoc) {
      throw new Error('Cannot access iframe document');
    }
    
    const bakedMark = await buildInvoiceBrandMarkDataUrl();

    // Write HTML to iframe (inline data URL — no broken remote / invisible logo)
    iframeDoc.open();
    iframeDoc.write(generateInvoiceHTML(invoice, lang, bakedMark));
    iframeDoc.close();

    // Wait for fonts + inline mark to render
    await new Promise(resolve => setTimeout(resolve, 350));
    
    // Get the invoice container from iframe
    const container = iframeDoc.querySelector('.invoice-container') as HTMLElement;
    if (!container) {
      throw new Error('Invoice container not found in iframe');
    }

    // Generate PDF using html2canvas and jsPDF
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const imgWidth = 210; // A4 width in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
    pdf.save(`${invoice.invoice_number}.pdf`);

    // Clean up iframe
    document.body.removeChild(iframe);

    // Mark as downloaded
    await markInvoiceDownloaded(invoice.id);
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
}

/**
 * Send invoice via email
 */
export async function sendInvoiceEmail(invoice: Invoice): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('User not authenticated');
      return false;
    }

    const invoiceHTML = generateInvoiceHTML(invoice, 'en');

    // Generate PDF on client-side
    console.log('🔄 Generating PDF for email attachment...');
    const pdfBase64 = await generateInvoicePdfBase64(invoice);
    
    if (!pdfBase64) {
      console.error('Failed to generate PDF');
      return false;
    }

    // Call Supabase Edge Function to send email with PDF attachment
    const { error } = await supabase.functions.invoke('send-invoice-email', {
      body: {
        invoiceId: invoice.id,
        invoiceHTML: invoiceHTML,
        pdfBase64: pdfBase64,
        invoiceNumber: invoice.invoice_number,
      },
    });

    if (error) {
      console.error('Error sending invoice email:', error);
      return false;
    }

    // Update invoice status
    await supabase
      .from('invoices')
      .update({ email_sent: true })
      .eq('id', invoice.id);

    console.log('✅ Invoice email sent successfully with PDF attachment');
    return true;
  } catch (error) {
    console.error('Unhandled error sending invoice email:', error);
    return false;
  }
}

/**
 * Generate PDF as base64 string for email attachment
 */
export async function generateInvoicePdfBase64(invoice: Invoice): Promise<string | null> {
  try {
    const html2canvas = (await import('html2canvas')).default;
    const { jsPDF } = await import('jspdf');

    // Create an iframe completely isolated from the main page to avoid visual glitch
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.left = '-9999px';
    iframe.style.top = '0';
    iframe.style.width = '210mm';
    iframe.style.height = '297mm';
    iframe.style.border = 'none';
    iframe.style.zIndex = '-9999';
    iframe.style.visibility = 'hidden';
    iframe.style.opacity = '0';
    
    // Add to body
    document.body.appendChild(iframe);
    
    // Wait for iframe to be ready
    await new Promise(resolve => setTimeout(resolve, 50));
    
    // Get iframe document
    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!iframeDoc) {
      throw new Error('Cannot access iframe document');
    }
    
    // Write HTML to iframe (English for email attachment — always legible for accounting)
    iframeDoc.open();
    iframeDoc.write(generateInvoiceHTML(invoice, 'en'));
    iframeDoc.close();
    
    // Wait for content to render
    await new Promise(resolve => setTimeout(resolve, 250));
    
    // Get the invoice container from iframe
    const container = iframeDoc.querySelector('.invoice-container') as HTMLElement;
    if (!container) {
      throw new Error('Invoice container not found in iframe');
    }

    // Generate canvas from HTML
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
    });

    // Clean up iframe
    document.body.removeChild(iframe);

    // Create PDF
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const imgWidth = 210; // A4 width in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

    // Get PDF as base64 string (without data:application/pdf;base64, prefix)
    const pdfBase64 = pdf.output('datauristring').split(',')[1];
    
    return pdfBase64;
  } catch (error) {
    console.error('Error generating PDF base64:', error);
    return null;
  }
}